import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import { QueryOption } from '../@types';
import { LocationNodeData } from '../@types/live-search.type';
import { PartTypeList, CSVExportPartType } from '../@types/part-type.type';
import {
  PartType,
  SoftwareFirmwareHardwareData,
  SoftwareFirmwareHardwareOptions,
  WarrantyData,
  ChildPartTypeData,
  SalePricingObject,
  SalesRepairSupplierData,
  PendingTransfersList,
  PartTypeLiveSearchItem
} from '../@types/partType.type';
import { SerialPopupObject } from '../@types/shipment.type';
import { RequestDataType } from '../@types/tlc.type';
import {
  createChildPartType,
  createPartType,
  createRepairPricing,
  createSalePricing,
  createSFHware,
  createSupplier,
  createWarranty,
  deleteChildPartType,
  getAllConfigurationHistory,
  getPartTypeById,
  getPartTypeManifestById,
  getPartTypesByClientContract,
  getPartTypeSFHwaresDetails,
  updateChildPartType,
  updatePartType,
  updateRepairPricing,
  updateSalePricing,
  updateSFHware,
  updateSupplier,
  updateWarranty,
  getPartTypeShipmentDataByPartTypeIdAndShipmentStatus,
  getSearchTLCPartTypes,
  getSearchTLCPartTypeLocation,
  getPartTypeByIdForService
} from '../api/partType';
import { getAllPartTypes } from '../api/partTypes';
import { PartTypeManifestKey , ChildPartTypesKey, ConfigurationHistoriesKey, PartTypesByClientContractKey, PartTypesKey, PartTypesSoftwareFirmwareHardwareOptionsKey, PartTypePendingTransfersKey } from '../configs/part-type';
import { ShipmentManifestSuppliersKey } from '../configs/shipment';

interface SalePricingMutationData extends Omit<SalePricingObject, 'id'> {
  id?: number;
  partTypeId?: number;
}

interface RepairSupplierMutationData extends SalesRepairSupplierData {
  partTypeId?: number;
}

export interface PartTypeListResponse {
  previousPage?: number;
  currentPage?: number;
  nextPage?: number;
  total?: number;
  limit?: number;
  data?: PartTypeList[];
  filterResponse?: CSVExportPartType[];
}

export interface ConfigurationHistoryListResponse {
  id: number;
  partTypeId: number;
  createdBy: string;
  createdAt: string;
  notes: string;
  configurationChangeNotes: string;
}

export const useGetAllPartTypes = (queryOption: QueryOption): UseQueryResult<PartTypeListResponse> => {
  return useQuery({
    queryKey: [PartTypesKey, queryOption],
    queryFn: () => getAllPartTypes(queryOption),
    gcTime: 0
  });
};

export const useGetOnePartType = (id: number): UseQueryResult<PartType | undefined> => {
  return useQuery({
    queryKey: [PartTypesKey, id],
    queryFn: () => getPartTypeById(id),
    enabled: !!id && id !== -1
  });
};

export const useGetOnePartTypeOnClose = (): UseMutationResult<PartType | undefined, Error, number> => {
  return useMutation({
    mutationFn: async (data: number) => {
      if (data && data !== -1) {
        return await getPartTypeById(data);
      }
      return undefined;
    }
  });
};

export const useGetOnePartTypeForService = (id: number): UseQueryResult<PartType | undefined> => {
  return useQuery({
    queryKey: [PartTypesKey, id],
    queryFn: () => getPartTypeByIdForService(id),
    enabled: !!id
  });
};

export const useGetAllConfigurationHistory = (id: number): UseQueryResult<ConfigurationHistoryListResponse[] | undefined> => {
  return useQuery({
    queryKey: [ConfigurationHistoriesKey, id],
    queryFn: () => getAllConfigurationHistory(id),
    enabled: !!id
  });
};

export const useCreatePartType = (): UseMutationResult<PartType, Error, Partial<PartType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<PartType>) => createPartType(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartTypesKey]
      });
    }
  });
};

export const useUpdatePartType = (): UseMutationResult<PartType, Error, Partial<PartType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<PartType>) => updatePartType(data.id || 0, data),

    onSuccess: async (response: PartType) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.id]
      });
    }
  });
};

export const useCreateWarranty = (): UseMutationResult<WarrantyData & { partTypeId: number }, Error, WarrantyData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WarrantyData) => createWarranty(data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useUpdateWarranty = (): UseMutationResult<WarrantyData & { partTypeId: number }, Error, WarrantyData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WarrantyData) => updateWarranty(data.id || 0, data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useCreateSupplier = (): UseMutationResult<SalesRepairSupplierData & { partTypeId: number }, Error, SalesRepairSupplierData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SalesRepairSupplierData) => createSupplier(data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentManifestSuppliersKey, response.partTypeId]
      });
    }
  });
};

export const useUpdateSupplier = (): UseMutationResult<SalesRepairSupplierData & { partTypeId: number }, Error, SalesRepairSupplierData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SalesRepairSupplierData) => updateSupplier(Number(data.id) || 0, data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentManifestSuppliersKey, response.partTypeId]
      });
    }
  });
};

export const useCreateSFHware = (): UseMutationResult<SoftwareFirmwareHardwareData & { partTypeId: number }, Error, SoftwareFirmwareHardwareData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SoftwareFirmwareHardwareData) => createSFHware(data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useUpdateSFHware = (): UseMutationResult<SoftwareFirmwareHardwareData & { partTypeId: number }, Error, SoftwareFirmwareHardwareData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SoftwareFirmwareHardwareData) => updateSFHware(data.id || 0, data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useCreateSalePricing = (): UseMutationResult<SalePricingObject & { partTypeId: number }, Error, SalePricingMutationData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SalePricingMutationData) => createSalePricing(data.partTypeId || 0, data as SalePricingObject),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useUpdateSalePricing = (): UseMutationResult<SalePricingObject & { partTypeId: number }, Error, SalePricingMutationData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SalePricingMutationData) => updateSalePricing(data.id || 0, data as SalePricingObject),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useCreateRepairPricing = (): UseMutationResult<SalesRepairSupplierData & { partTypeId: number }, Error, RepairSupplierMutationData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RepairSupplierMutationData) => createRepairPricing(data.partTypeId || 0, data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useUpdateRepairPricing = (): UseMutationResult<SalesRepairSupplierData & { partTypeId: number }, Error, RepairSupplierMutationData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RepairSupplierMutationData) => updateRepairPricing(Number(data.id) || 0, data),

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, response.partTypeId]
      });
    }
  });
};

export const useCreateChildPartType = (partTypeId: number): UseMutationResult<ChildPartTypeData, Error, ChildPartTypeData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChildPartTypeData) => createChildPartType(partTypeId, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, partTypeId]
      });
    }
  });
};

export const useUpdateChildPartType = (partTypeId: number): UseMutationResult<ChildPartTypeData, Error, ChildPartTypeData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChildPartTypeData) => updateChildPartType(partTypeId, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, partTypeId]
      });
    }
  });
};

export const useDeleteChildPartType = (partTypeId: number): UseMutationResult<void, Error, { childPartTypeId?: number }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { childPartTypeId?: number }) => deleteChildPartType(partTypeId, data.childPartTypeId || 0),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ChildPartTypesKey, partTypeId]
      });
    }
  });
};

export const useGetPartTypesByClientContract = (clientId: number, contractId?: number): UseQueryResult<PartTypeLiveSearchItem[]> => {
  return useQuery({
    queryKey: [PartTypesByClientContractKey, clientId, contractId || 0],
    queryFn: () => getPartTypesByClientContract(clientId, contractId),
    enabled: false
  });
};

export const useGetSoftwareFirmwareHardwareOptions = (partTypeId: number): UseQueryResult<SoftwareFirmwareHardwareOptions> => {
  return useQuery({
    queryKey: [PartTypesSoftwareFirmwareHardwareOptionsKey, partTypeId],
    queryFn: () => getPartTypeSFHwaresDetails(partTypeId),
    enabled: false
  });
};

export const useGetPartTypeManifestById = (id: number): UseQueryResult<SerialPopupObject | undefined> => {
  return useQuery({
    queryKey: [PartTypeManifestKey],
    queryFn: () => getPartTypeManifestById(id),
    enabled: id > 0
  });
};

export const useGetPartTypeShipmentDataByPartTypeIdAndShipmentStatus = (partTypeId: number, isSerialized: boolean, shipmentStatusCodes: string): UseQueryResult<PendingTransfersList[] | undefined> => {
  return useQuery({
    queryKey: [PartTypePendingTransfersKey, partTypeId],
    queryFn: () => getPartTypeShipmentDataByPartTypeIdAndShipmentStatus(partTypeId, isSerialized, shipmentStatusCodes),
    enabled: !!partTypeId
  });
};

export const useGetSearchTLCPartTypes = (): UseMutationResult<PartType[], Error, string> => {
  return useMutation({
    mutationFn: (data: string) => getSearchTLCPartTypes(data),
  });
};

export const useGetSearchTLCPartTypeLocation = (): UseMutationResult<LocationNodeData[], Error, RequestDataType> => {
  return useMutation({
    mutationFn: (data: RequestDataType) => getSearchTLCPartTypeLocation(data),
  });
};
